import React, { useEffect, useState } from 'react';
import { Input, Button, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { IconWand } from '@tabler/icons-react';
import { message } from '../../services/toastMessage';
import { createQuoteDraft } from '../../services/QuoteDraftService';
import { IQuoteDraftLine } from '../../interfaces/heavyDutyMachinery/IQuoteDraft';
import { IQuoteDraftEditLine, toDraftContextLines } from '../../helpers/quoteDraftHelper';
import { useSpeechDictation } from '../../hooks/useSpeechDictation';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { SURFACE_1 } from '../../theme/creamyTokens';
import DictateButton from '../DictateButton';

const { Text } = Typography;

/** Same floor the backend refuses below. "Quita el transporte" is a whole instruction; "sube" is not. */
const MIN_INSTRUCTION_LENGTH = 3;

interface QuoteVoiceEditProps {
  lines: IQuoteDraftEditLine[];
  /** The whole revised quote, as the assistant returned it. */
  onApplied: (lines: IQuoteDraftLine[], notes?: string) => void;
  trade?: string;
}

/**
 * Say what to change, and the quote changes.
 *
 * The reason this earns its place over just editing the fields: a provider
 * doing this is usually not sitting at a desk. They are in a van, or in
 * somebody's garden with the customer standing next to them saying "y ya que
 * estás, ponme también la arqueta". Reaching for four numeric fields with one
 * muddy thumb is the moment the whole idea falls apart, and "añade una arqueta
 * y sube la hora a cincuenta" is one sentence.
 *
 * It always goes through the text box on the way. Dictation gets a word wrong
 * often enough that sending straight from the microphone would mean the
 * provider finds out what was heard by looking at what changed -- so the
 * transcript lands in a field they can see and fix, and the change happens
 * when they press the button.
 */
const QuoteVoiceEdit: React.FC<QuoteVoiceEditProps> = ({ lines, onApplied, trade }) => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();
  const dictation = useSpeechDictation(i18n.language);

  const [instruction, setInstruction] = useState('');
  const [working, setWorking] = useState(false);

  // What was heard goes into the field, where it can be read and corrected
  // before it does anything.
  useEffect(() => {
    if (dictation.transcript) setInstruction(dictation.transcript);
  }, [dictation.transcript]);

  useEffect(() => {
    if (dictation.error) message.error(translate(t, TRANSLATIONS.DICTATE_ERROR));
  }, [dictation.error, t]);

  const handleApply = async () => {
    const said = instruction.trim();
    if (said.length < MIN_INSTRUCTION_LENGTH) {
      message.error(translate(t, TRANSLATIONS.QUOTE_VOICE_EDIT_TOO_SHORT));
      return;
    }
    dictation.stop();

    setWorking(true);
    try {
      const outcome = await createQuoteDraft({
        description: '',
        trade,
        instruction: said,
        currentLines: toDraftContextLines(lines),
      });

      if (!outcome.ok) {
        if (outcome.error) console.error('Voice quote edit failed:', outcome.error);
        message.error(translate(t, TRANSLATIONS.QUOTE_VOICE_EDIT_ERROR));
        return;
      }

      onApplied(outcome.draft.lines, outcome.draft.notes);
      setInstruction('');
      dictation.reset();
    } finally {
      setWorking(false);
    }
  };

  return (
    <div style={{ padding: 12, marginBottom: 14, borderRadius: 14, background: SURFACE_1 }}>
      <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>
        {translate(t, TRANSLATIONS.QUOTE_VOICE_EDIT_TITLE)}
      </Text>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Input
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          onPressEnter={handleApply}
          placeholder={translate(
            t,
            dictation.listening ? TRANSLATIONS.QUOTE_VOICE_EDIT_LISTENING : TRANSLATIONS.QUOTE_VOICE_EDIT_PLACEHOLDER
          )}
          maxLength={1000}
          style={{ flex: '1 1 180px', minWidth: 0, borderRadius: 999, background: token.colorBgContainer }}
        />
        <DictateButton
          supported={dictation.supported}
          listening={dictation.listening}
          onStart={() => dictation.start(instruction)}
          onStop={dictation.stop}
          disabled={working}
        />
        <Button
          type="primary"
          icon={<IconWand size={15} />}
          loading={working}
          onClick={handleApply}
          style={{ borderRadius: 999 }}
        >
          {translate(t, TRANSLATIONS.QUOTE_VOICE_EDIT_APPLY)}
        </Button>
      </div>

      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
        {translate(t, TRANSLATIONS.QUOTE_VOICE_EDIT_HINT)}
      </Text>
    </div>
  );
};

export default QuoteVoiceEdit;
